/**
 * Virtual Try-On — on-demand loader.
 *
 * The engine (assets/vto-state.js + assets/vto.js, ~192 KB) used to be a pair
 * of deferred <script>s on every page. This small file replaces them. It loads
 * the engine:
 *
 *   1. At once, when this tab carries a try-on that has to keep running or be
 *      shown: a render still generating (the request must be re-attached
 *      before its 230 s window closes, on whatever page the shopper is on), or
 *      a finished/failed look the shopper has not seen yet (the tile wears
 *      it). Same rule after a back/forward-cache restore. Also with
 *      ?vto-debug=1.
 *   2. On the first tap on any [data-vto-trigger]. The tap is remembered and,
 *      once the engine has restored the saved photo and session, handed to it
 *      (window.SunyaVto.open) — the same call its own delegated click listener
 *      makes — so the sheet opens exactly as it would have. The click itself
 *      is left to propagate normally, so analytics see one real click, as
 *      before. Pointer-down and keyboard focus on a trigger start the download
 *      a beat early.
 *   3. At idle after load where the gallery pill shows (product pages), so
 *      the first tap is as quick as the next; as a download only (rel=prefetch,
 *      never executed) on other pages that show a trigger. Neither with
 *      Save-Data on or on 2G.
 *
 * It also owns the one-time attention glow on the gallery pill, which used to
 * run from the engine's constructor and must not wait for a tap.
 *
 * Script tag (layout/theme.liquid) supplies the URLs:
 *   data-state-src, data-engine-src, data-api-origin
 */
(function () {
  'use strict';
  if (window.SunyaVtoLoader) return;

  // currentScript is set for a classic deferred script; the query is a
  // fallback in case the tag is ever changed to a module.
  var me = document.currentScript || document.querySelector('script[data-engine-src]');
  var STATE_SRC = me && me.getAttribute('data-state-src');
  var ENGINE_SRC = me && me.getAttribute('data-engine-src');
  var API_ORIGIN = me && me.getAttribute('data-api-origin');
  if (!STATE_SRC || !ENGINE_SRC) return;

  var TRIGGER_SEL = '[data-vto-trigger]';
  var SESSION_KEY = 'vto_active_session_state';   // written by vto.js saveActiveSessionSync()
  var HINT_KEY = 'vto_hint_v1';                    // shared with vto.js readHintState()
  var HINT_MAX_SHOWS = 3;
  var HINT_LIFETIME_MS = 3000;
  var REPLAY_WINDOW_MS = 10000;  // a tap older than this is dropped: the shopper has moved on
  var DEBUG = /[?&]vto-debug=1/.test(location.search);

  var enginePromise = null;
  var engineReady = false;
  var pending = null;            // { el, at } — the tap waiting for the engine
  var api = {
    hintHandled: false,
    load: loadEngine,
    get ready() { return engineReady; },
  };
  window.SunyaVtoLoader = api;

  // ── Session check (mirrors vto.js restoreFromStorage) ─────────────
  function readSession() {
    try {
      var raw = sessionStorage.getItem(SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }
  // True when the engine, once running, would re-attach a render or dress a
  // tile: exactly the cases restoreFromStorage() acts on visibly.
  function sessionNeedsEngine(s) {
    if (!s || typeof s !== 'object') return false;
    if (s.state === 'generating') return true;
    if ((s.state === 'results' || s.state === 'error') && !s.dismissed) return true;
    return false;
  }

  // ── Engine ────────────────────────────────────────────────────────
  function addScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = false;           // vto-state.js must execute before vto.js
      s.onload = function () { resolve(); };
      s.onerror = function () {
        if (s.parentNode) s.parentNode.removeChild(s);
        reject(new Error('[vto-loader] failed: ' + src));
      };
      document.body.appendChild(s);
    });
  }

  function addPreconnect() {
    if (!API_ORIGIN || document.querySelector('link[rel="preconnect"][href="' + API_ORIGIN + '"]')) return;
    var l = document.createElement('link');
    l.rel = 'preconnect';
    l.href = API_ORIGIN;
    l.crossOrigin = 'anonymous';
    document.head.appendChild(l);
  }

  function waitForEngine() {
    return new Promise(function (resolve) {
      if (window.SunyaVto && window.SunyaVto.ready) { window.SunyaVto.ready.then(resolve, resolve); return; }
      document.addEventListener('vto:ready', function () { resolve(); }, { once: true });
    });
  }

  function loadEngine() {
    if (enginePromise) return enginePromise;
    addPreconnect();
    var tasks = [];
    // vto.js may already be on the page (an old cached layout); never run it twice.
    if (!window.VtoStateMachine) tasks.push(addScript(STATE_SRC));
    if (!window.SunyaVto) tasks.push(addScript(ENGINE_SRC));
    enginePromise = Promise.all(tasks)
      .then(waitForEngine)
      .then(function () {
        engineReady = true;
        stopListening();
        return window.SunyaVto;
      }, function (err) {
        enginePromise = null;      // the next tap retries
        throw err;
      });
    return enginePromise;
  }

  // ── First tap ─────────────────────────────────────────────────────
  function triggerFrom(e) {
    var t = e.target;
    return t && t.closest ? t.closest(TRIGGER_SEL) : null;
  }

  // Styled in assets/vto.css ([data-vto-trigger][aria-busy="true"]).
  function setBusy(el, on) {
    if (!el) return;
    if (on) el.setAttribute('aria-busy', 'true');
    else el.removeAttribute('aria-busy');
  }

  function onClick(e) {
    // Once vto.js has run, its own delegated listener owns every tap. It binds
    // in the same synchronous run that sets window.SunyaVto, so no tap is
    // handled by both.
    if (engineReady || window.SunyaVto) return;
    var trigger = triggerFrom(e);
    if (!trigger) return;
    e.preventDefault();            // as vto.js does (the triggers are type=button)
    if (pending) return;           // a second tap while loading changes nothing
    pending = { el: trigger, at: Date.now() };
    setBusy(trigger, true);
    loadEngine().then(function (vto) {
      var p = pending;
      pending = null;
      setBusy(p.el, false);
      // Skip the replay when the sheet is already open: a second tap that
      // landed after vto.js bound its own listener has opened it already.
      var modal = document.getElementById('VtoModal');
      var alreadyOpen = modal && !modal.hidden;
      if (!alreadyOpen && vto && vto.open && p.el.isConnected && Date.now() - p.at < REPLAY_WINDOW_MS) {
        Promise.resolve(vto.open(p.el)).catch(function (err) {
          if (window.console) console.warn('[vto-loader] open failed:', err && err.message);
        });
      }
    }, function (err) {
      var p = pending;
      pending = null;
      if (p) setBusy(p.el, false);
      if (window.console) console.warn(err && err.message);
    });
  }

  // Pointer-down and keyboard focus land a beat before the click: start then.
  // (Not hover: the gallery pill sits on the hero photo, and running the
  // engine for every mouse that crosses it would undo the point of this file.)
  function onIntent(e) {
    if (enginePromise || !triggerFrom(e)) return;
    loadEngine().catch(function () {});
  }

  var intentEvents = ['pointerdown', 'focusin'];
  function stopListening() {
    document.removeEventListener('click', onClick);
    intentEvents.forEach(function (t) { window.removeEventListener(t, onIntent, true); });
  }
  // Same place and phase as vto.js's own listener (document, bubbling).
  document.addEventListener('click', onClick);
  intentEvents.forEach(function (t) { window.addEventListener(t, onIntent, { capture: true, passive: true }); });

  // ── Attention glow (moved from vto.js maybeShowHint) ─────────────
  function readHintState() {
    try {
      var raw = JSON.parse(localStorage.getItem(HINT_KEY) || 'null');
      if (!raw || typeof raw !== 'object') return { shows: 0, done: false };
      var shows = Number(raw.shows);
      return { shows: shows >= 0 && shows < 1e4 ? Math.floor(shows) : 0, done: raw.done === true };
    } catch (e) { return { shows: 0, done: false }; }
  }
  function writeHintState(s) {
    try { localStorage.setItem(HINT_KEY, JSON.stringify(s)); } catch (e) { /* not fatal */ }
  }
  function maybeShowHint() {
    var el = document.querySelector('[data-vto-hintable]');
    if (!el || el.dataset.vtoTileState) return;
    var state = readHintState();
    if (state.done || state.shows >= HINT_MAX_SHOWS) return;
    writeHintState({ shows: state.shows + 1, done: false });
    api.hintHandled = true;
    el.dataset.vtoHint = '1';
    setTimeout(function () { delete el.dataset.vtoHint; }, HINT_LIFETIME_MS);
  }

  // ── Warm-up at idle, where a trigger is on the page ───────────────
  // Where the gallery pill shows (a product page), the engine is loaded and
  // run, not just downloaded: with a download only, the shopper's first tap
  // still waited for the browser to parse, compile and run ~192 KB before the
  // sheet could move (about 0.4 s more than every later tap on a mid-range
  // phone). Running it opens nothing: it restores the saved photo and binds
  // its listeners, and the sheet opens on a tap as always. Elsewhere (a
  // listing whose cards carry triggers) it is downloaded only. Neither with
  // Save-Data on or on a 2G connection.
  function warmUpEngine() {
    if (enginePromise || !document.querySelector(TRIGGER_SEL)) return;
    var c = navigator.connection;
    if (c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || ''))) return;
    var pill = document.querySelector('[data-vto-hintable]');
    if (pill && pill.getClientRects().length) {
      loadEngine().catch(function (err) { if (window.console) console.warn(err && err.message); });
      return;
    }
    [STATE_SRC, ENGINE_SRC].forEach(function (href) {
      var l = document.createElement('link');
      l.rel = 'prefetch';
      l.as = 'script';
      l.href = href;
      document.head.appendChild(l);
    });
  }
  function afterLoadIdle(fn) {
    var run = function () {
      if ('requestIdleCallback' in window) requestIdleCallback(fn, { timeout: 5000 });
      else setTimeout(fn, 1500);
    };
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  }

  // ── Start ─────────────────────────────────────────────────────────
  function start() {
    if (DEBUG || sessionNeedsEngine(readSession())) {
      loadEngine().catch(function (err) { if (window.console) console.warn(err && err.message); });
      return;                      // the engine dresses the tile; no glow over it
    }
    maybeShowHint();
    afterLoadIdle(warmUpEngine);
  }

  // A page restored from the back/forward cache keeps this loader's state.
  // If the shopper started or finished a render on a page they then went
  // Back from, bring the engine in to pick it up.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted || enginePromise) return;
    if (sessionNeedsEngine(readSession())) loadEngine().catch(function () {});
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
